// Hàm thuần dùng chung của khung Trợ lý AI ở ⑤ (gốc: chat sửa tập 260928 E3-P4; drawer cũ đã gỡ ở 261005 E2-P2b): chip, thẻ xem thử,
// thẻ nối tập, diff, phiên bản, lỗi API. Component (`workbench/assistant/*`) chỉ vẽ lại. KHÔNG import antd (antd v6 + react-router v7 không load được dưới jest của react-scripts — xem CLAUDE.md).
import type {
  EpisodeFixIssueChip, EpisodeFixMessage, EpisodeFixNewLineSpeaker, EpisodeFixPreviewPayload, EpisodeFixRelinkPayload,
  EpisodeScriptRecord, ScriptVersionOrigin,
} from '../../types/adaptation';
import { friendlyApiMessage, friendlyJobNote } from './workbench/jobErrorText';
import { redactMoney } from './workbench/moneyRedact';

const str = (v: unknown): string => String(v ?? '').trim();

export const MAX_FIX_TEXT = 2000;
export const MIN_RELINK_REASON = 5;

// ── chip + gửi chat ──────────────────────────────────────────────────────────────

export const chipSceneLabel = (chip: Pick<EpisodeFixIssueChip, 'scenePlanId'>, scenes: ReadonlyArray<{ scenePlanId: string; index: number }>): string => {
  if (!chip.scenePlanId) return 'cả tập';
  const s = scenes.find((x) => x.scenePlanId === chip.scenePlanId);
  return `cảnh ${s ? s.index : chip.scenePlanId}`;
};

export const toggleSelected = (selected: readonly string[], id: string): string[] =>
  selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];

export const editBlockerText = (blocker: string | null | undefined): string | null => {
  switch (blocker) {
    case null: case undefined: case '': return null;
    case 'SCENE_PLAN_MISSING': return 'Tập này viết từ bản cũ (thiếu scenePlan) nên không sửa từng cảnh được — chỉ "Chạy lại tập".';
    case 'SCENE_IDS_MISSING': return 'Tập này viết từ bản cũ (cảnh thiếu scenePlanId) nên không sửa từng cảnh được — chỉ "Chạy lại tập".';
    case 'NOT_DRAFT': return 'Tập này không ở trạng thái DRAFT — chỉ sửa được bản DRAFT.';
    default: return `Không sửa được tập này (${blocker}).`;
  }
};

/** Nút "Sửa tập" ở HÀNG tập: lý do không mở được (hoặc null). Chỉ để định hướng — cổng thật ở máy chủ. */
export const episodeRowFixBlocker = (sc: Pick<EpisodeScriptRecord, 'status' | 'script'> | undefined | null): string | null => {
  if (!sc) return 'Tập chưa có kịch bản';
  if (sc.status !== 'DRAFT' || !sc.script) return 'Tập hỏng / chưa phải DRAFT — chỉ sửa được bản DRAFT';
  const scenes = Array.isArray(sc.script.scenes) ? sc.script.scenes : [];
  if (!scenes.length || !Array.isArray(sc.script.scenePlan) || !sc.script.scenePlan.length || scenes.some((s) => !str(s?.scenePlanId))) {
    return 'Kịch bản cũ (thiếu scenePlan / id cảnh) — chỉ "Chạy lại tập"';
  }
  return null;
};

export type PreviewPhase = 'running' | 'failed' | 'ready' | 'applied' | 'stale' | 'dismissed';

export interface PreviewView {
  phase: PreviewPhase;
  tone: 'ok' | 'warn' | 'danger' | 'neutral';
  canApply: boolean;
  canDismiss: boolean;
  headline: string;
  criticLabel: string;
  violationsLabel: string;
  wordsLabel: string;
  warnings: string[];
  errorText?: string;
}

/** 261015 P4c-2c-2: câu cho MỘT cảnh bị loại khỏi ứng viên theo `reason` (trước đây cố định "thiếu người nói" cho mọi lý do). Lý do lạ ⇒ câu trung tính. */
export const droppedSceneText = (scenePlanId: string, reason: string | undefined): string => {
  if (reason === 'INTRO_EMPTIED') return `Cảnh ${scenePlanId} bị loại khỏi bản này vì cảnh chỉ còn câu giới thiệu không hợp lệ — giữ cảnh cũ.`;
  if (reason === 'SPEAKER_MISSING') return `Cảnh ${scenePlanId} bị loại khỏi bản này vì câu thoại/V.O. thiếu người nói hợp lệ — giữ nguyên bản cũ.`;
  return `Cảnh ${scenePlanId} bị loại khỏi bản này vì không hợp lệ — giữ nguyên bản cũ.`;
};

export const previewView = (m: EpisodeFixMessage, nextEpisode: number): PreviewView => {
  const p = (m.payload ?? {}) as Partial<EpisodeFixPreviewPayload>;
  const blank = { headline: '', criticLabel: '', violationsLabel: '', wordsLabel: '', warnings: [] as string[] };
  // 261015: BE cho DISMISS từ PREVIEW_RUNNING (BLOCK_EDGES) — khối kẹt "đang viết thử" phải bỏ được; kết quả job về sau bị CAS bỏ qua.
  if (m.status === 'RUNNING') return { phase: 'running', tone: 'neutral', canApply: false, canDismiss: true, ...blank, headline: 'Đang viết thử các cảnh đích…' };
  if (m.status === 'FAILED') return { phase: 'failed', tone: 'danger', canApply: false, canDismiss: true, ...blank, headline: 'Xem thử không chạy được', errorText: p.error };
  if (m.status === 'APPLIED') return { phase: 'applied', tone: 'ok', canApply: false, canDismiss: false, ...blank, headline: 'Đã áp' };
  if (m.status === 'STALE') return { phase: 'stale', tone: 'neutral', canApply: false, canDismiss: false, ...blank, headline: 'Đã cũ — tập đã đổi sau lần xem thử này' };
  if (m.status === 'DISMISSED') return { phase: 'dismissed', tone: 'neutral', canApply: false, canDismiss: false, ...blank, headline: 'Đã bỏ' };

  const changed = p.diff?.changed.length ?? 0;
  const critical = p.criticalCount ?? 0;
  const warnings: string[] = [];
  if (p.verdict === 'OUTSIDE_CHANGED') warnings.push(`Có cảnh NGOÀI đích bị đổi (${(p.outsideChanged ?? []).join(', ')}) — không áp bản này.`);
  if (p.verdict === 'CRITICAL_UP') warnings.push(`Critic báo ${critical} lỗi nặng ở bản mới — không nên áp (tham khảo — critic dao động; bạn quyết).`);
  if (p.criticFailed) warnings.push('Chưa chấm được bằng critic — chưa có số lỗi nặng.');
  if (p.exitChanged) warnings.push(`Đoạn kết đổi — sau khi áp, AI sẽ kiểm nối với tập ${nextEpisode} (kết quả ở thẻ "Nối tập ${nextEpisode}").`);
  for (const d of p.droppedScenes ?? []) warnings.push(droppedSceneText(d.scenePlanId, d.reason));
  const tone: PreviewView['tone'] = p.verdict === 'OUTSIDE_CHANGED' || p.verdict === 'CRITICAL_UP' ? 'danger' : warnings.length ? 'warn' : 'ok';
  return {
    phase: 'ready', tone, canApply: p.verdict !== 'OUTSIDE_CHANGED', canDismiss: true,
    headline: `${changed} cảnh đổi`,
    criticLabel: `critic: ${critical} lỗi nặng · ${p.criticNote || 'tham khảo — critic dao động'}`,
    violationsLabel: `cảnh báo ${p.violationsBefore ?? 0} → ${p.violationsAfter ?? 0}`,
    wordsLabel: `từ nói ${p.wordCountBefore ?? 0} → ${p.wordCountAfter ?? 0}`,
    warnings,
  };
};

export interface DiffLine { op: 'same' | 'del' | 'add'; text: string }

/** Diff theo dòng (LCS): giữ phần chung, `del` = dòng bản cũ, `add` = dòng bản mới. Đủ cho cảnh vài chục dòng. */
export const lineDiff = (before: readonly string[], after: readonly string[]): DiffLine[] => {
  const n = before.length; const m = after.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) dp[i][j] = before[i] === after[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const out: DiffLine[] = [];
  let i = 0; let j = 0;
  while (i < n && j < m) {
    if (before[i] === after[j]) { out.push({ op: 'same', text: before[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ op: 'del', text: before[i] }); i++; }
    else { out.push({ op: 'add', text: after[j] }); j++; }
  }
  while (i < n) out.push({ op: 'del', text: before[i++] });
  while (j < m) out.push({ op: 'add', text: after[j++] });
  return out;
};

/** Người nói của từng câu MỚI, gom theo cảnh — để người soát "đúng người chưa" (chốt người nói chỉ kiểm id thuộc cast, không kiểm đúng người). */
export const newLinesByScene = (speakers: readonly EpisodeFixNewLineSpeaker[]): Array<{ scenePlanId: string; lines: string[] }> => {
  const out: Array<{ scenePlanId: string; lines: string[] }> = [];
  for (const s of speakers) {
    let g = out.find((x) => x.scenePlanId === s.scenePlanId);
    if (!g) { g = { scenePlanId: s.scenePlanId, lines: [] }; out.push(g); }
    g.lines.push(s.kind === 'voiceover' ? `${s.name} (lời kể): ${s.text}` : `${s.name}: ${s.text}`);
  }
  return out;
};

// ── thẻ nối tập (R9) ─────────────────────────────────────────────────────────────

export const isReasonValid = (reason: unknown): boolean => str(reason).length >= MIN_RELINK_REASON;

export interface RelinkView {
  phase: 'running' | 'verdict' | 'done';
  tone: 'ok' | 'warn' | 'danger' | 'neutral';
  headline: string;
  nextEpisode: number;
  needsReason: boolean;
  canRelink: boolean;
  /** "Viết lại tuần tự từ tập N+1" — đường có sẵn; chưa nối xong thì lúc nào cũng cho. */
  canRewrite: boolean;
  /** 261015 P4a (D9): thẻ do HỆ THỐNG tự nối (AI xác nhận khớp) ⇒ có nút "Hoàn tác". Chỉ `RELINKED` + `relinkedVia === 'AI'`; cổng bấm thật ở `undoAutoState`. */
  canUndoAuto: boolean;
  conflicts: Array<{ quoteNew: string; quoteNext: string; problem: string }>;
  invalidNote: string | null;
  newExitText: string;
  oldExitText: string;
  nextOpeningText: string;
  error?: string;
}

/** Quyết định hiển thị của MỘT thẻ nối tập (khung Trợ lý đọc từ `film_adaptation_relinks`). */
export const relinkCardView = (card: Partial<EpisodeFixRelinkPayload> | null | undefined): RelinkView => {
  const p = card ?? {};
  const nextEpisode = p.nextEpisode ?? 0;
  const base = { nextEpisode, canUndoAuto: false, newExitText: p.newExitText ?? '', oldExitText: p.oldExitText ?? '', nextOpeningText: p.nextOpeningText ?? '', conflicts: [] as RelinkView['conflicts'], invalidNote: null as string | null };
  if (p.state === 'RUNNING') return { ...base, phase: 'running', tone: 'neutral', headline: `AI đang kiểm đoạn kết mới có nối được với tập ${nextEpisode}…`, needsReason: false, canRelink: false, canRewrite: false };
  if (p.state === 'RELINKED') {
    // 261015 P4a (D9): tự nối = HỆ THỐNG làm sau khi AI xác nhận khớp — không có "người" để ghi (relinkedBy chỉ là giá trị hệ thống) ⇒ không hiện "bởi …".
    if (p.relinkedVia === 'AI') {
      return { ...base, phase: 'done', tone: 'ok', headline: `Tập ${nextEpisode} đã tự nối — AI xác nhận khớp với đoạn kết mới. Nhớ cho AI đọc lại cả mùa`, needsReason: false, canRelink: false, canRewrite: false, canUndoAuto: true };
    }
    return { ...base, phase: 'done', tone: 'ok', headline: `Tập ${nextEpisode} đã được nối lại${p.relinkedBy ? ` (bởi ${p.relinkedBy})` : ''} — nhớ cho AI đọc lại cả mùa`, needsReason: false, canRelink: false, canRewrite: false };
  }
  if (p.state === 'AI_CONSISTENT') return { ...base, phase: 'verdict', tone: 'ok', headline: `AI: đoạn kết mới vẫn nhất quán với 2 cảnh đầu tập ${nextEpisode}`, needsReason: false, canRelink: true, canRewrite: true };
  if (p.state === 'AI_INCONSISTENT') {
    const invalid = (p.invalidConflicts ?? []).length;
    return {
      ...base, phase: 'verdict', tone: 'danger', headline: `AI: đoạn kết mới có thể mâu thuẫn với tập ${nextEpisode}`, needsReason: true, canRelink: true, canRewrite: true,
      conflicts: (p.conflicts ?? []).map((c) => ({ quoteNew: c.quoteNew, quoteNext: c.quoteNext, problem: c.problem })),
      invalidNote: invalid ? `${invalid} chỗ AI nêu mà trích dẫn không khớp nguyên văn (không tin được, chỉ để tham khảo)` : null,
    };
  }
  if (p.state === 'AI_FAILED') return { ...base, phase: 'verdict', tone: 'warn', headline: 'AI chưa kiểm được đoạn kết này', needsReason: true, canRelink: true, canRewrite: true, error: friendlyJobNote(p.error) || undefined };
  return { ...base, phase: 'done', tone: 'neutral', headline: 'Thẻ nối tập đã cũ', needsReason: false, canRelink: false, canRewrite: false };
};

// ── phiên bản + lỗi ─────────────────────────────────────────────

const ORIGIN_LABEL: Record<ScriptVersionOrigin, string> = {
  GENERATED: 'AI viết', CHAT_EDIT: 'Chat sửa', MANUAL_EDIT: 'Sửa tay', RESTORE: 'Khôi phục', RELINK: 'Nối tập', LOCALIZE_RENAME: 'Đổi tên ở ③', RECEIPT: 'Biên lai gieo', AUTO_FIX: 'Máy tự sửa',
};
/** 261005 E3-P1b — nhãn phiên bản khung xương ② (kho riêng, thêm BASELINE/MERGE; CHAT_EDIT = "Trợ lý"). */
const SKELETON_ORIGIN_LABEL: Record<string, string> = {
  BASELINE: 'Bản sớm nhất (trước khi có phiên bản)', GENERATED: 'AI dựng', MERGE: 'Gộp khung xương', MANUAL_EDIT: 'Sửa tay', CHAT_EDIT: 'Trợ lý', RESTORE: 'Khôi phục', AUTO_FIX: 'Máy sửa (chấm khung)',
};
/** 261005 E3-P2b — nhãn phiên bản chia tập ④ (kho riêng: GENERATED/BASELINE/REBUILD/MANUAL_EDIT/CHAT_EDIT/RESTORE). */
const SPLIT_ORIGIN_LABEL: Record<string, string> = {
  BASELINE: 'Bản sớm nhất (trước khi có phiên bản)', GENERATED: 'Chia tập lần đầu', REBUILD: 'Chia lại tập', MANUAL_EDIT: 'Sửa tay', CHAT_EDIT: 'Trợ lý', RESTORE: 'Khôi phục',
  // 261015 P3a: `APPEND` đã có từ E4-P4a nhưng thiếu nhãn (hiện mã thô); `GATE_FIX` = cổng AI tự sửa tập lỗi (hoàn tác được như mọi phiên bản).
  APPEND: 'Chia phần mới', GATE_FIX: 'Tự sửa sau chấm',
};
/** 261005 E3-P3a — nhãn phiên bản bản địa hoá ③ (kho riêng: GENERATED/BASELINE/REDO/MANUAL_EDIT/CHAT_EDIT/RESTORE). */
const LOCALIZE_ORIGIN_LABEL: Record<string, string> = {
  BASELINE: 'Bản sớm nhất (trước khi có phiên bản)', GENERATED: 'AI bản địa hoá', REDO: 'AI làm lại', MANUAL_EDIT: 'Sửa tay', CHAT_EDIT: 'Trợ lý', RESTORE: 'Khôi phục',
};
/** 261005 E3-P4b1 — nhãn phiên bản chương thô ① (`op` của kho riêng: RELOAD/SPLIT/MERGE/DELETE/EDIT/RESTORE). */
const SOURCE_ORIGIN_LABEL: Record<string, string> = { RELOAD: 'Tải lại', SPLIT: 'Tách chương', MERGE: 'Gộp chương', DELETE: 'Xoá chương', EDIT: 'Sửa tay', RESTORE: 'Khôi phục' };
type VersionStage = 'script' | 'skeleton' | 'split' | 'localize' | 'source';
export const versionOriginLabel = (o: string, stageKey: VersionStage = 'script'): string =>
  (stageKey === 'skeleton' ? SKELETON_ORIGIN_LABEL[o] : stageKey === 'split' ? SPLIT_ORIGIN_LABEL[o] : stageKey === 'localize' ? LOCALIZE_ORIGIN_LABEL[o] : stageKey === 'source' ? SOURCE_ORIGIN_LABEL[o] : ORIGIN_LABEL[o as ScriptVersionOrigin]) ?? String(o);
export const versionTitle = (v: { versionNo: number; origin: string; note: string | null }, stageKey: VersionStage = 'script'): string =>
  [`v${v.versionNo}`, versionOriginLabel(v.origin, stageKey), v.note ? str(v.note) : ''].filter(Boolean).join(' · ');

const CODE_TEXT: Record<string, (server: string) => string> = {
  SCRIPT_CHANGED: () => 'Kịch bản tập này đã đổi từ lúc bạn mở (có người/việc khác vừa ghi) — tải lại rồi thử lại.',
  JOB_RUNNING: (s) => `Đang có việc viết/đọc kịch bản chạy cho truyện này — chờ xong rồi thử lại. ${redactMoney(s)}`.trim(),
  REASON_REQUIRED: () => `Cần nhập lý do (≥ ${MIN_RELINK_REASON} ký tự) để nối lại khi AI không xác nhận nhất quán.`,
  PREVIEW_STALE: () => 'Xem thử này đã cũ (tập đã đổi sau đó) — xem thử lại.',
  VERDICT_STALE: () => 'Đoạn kết đã đổi từ lúc AI kiểm — phán quyết cũ không dùng được; kiểm lại ở lần áp kế tiếp.',
  NOT_DRAFT: () => 'Tập không còn ở trạng thái DRAFT.',
  // 261015 P4a (D9) — Hoàn tác tự nối.
  NOT_AUTO_RELINK: () => 'Thẻ này không phải do hệ thống tự nối (hoặc đã hoàn tác rồi) — tải lại để xem trạng thái mới.',
  RELINK_SUPERSEDED: () => 'Tập sau đã được ghi thêm (hoặc thẻ nối đã bị thay) kể từ lần tự nối — không hoàn tác tự động được; muốn quay lại thì khôi phục thủ công ở mục Phiên bản.',
  VERSION_NOT_FOUND: () => 'Không còn phiên bản liền trước lần tự nối (đã bị cắt bớt) — không hoàn tác tự động được; dùng mục Phiên bản nếu cần.',
  STATE_CONFLICT: () => 'Thẻ nối tập vừa đổi trạng thái ở nơi khác — đã tải lại, xem rồi thử lại nếu cần.',
};

export const fixErrorText = (err: unknown): string => {
  const resp = (err as { response?: { status?: number; data?: { code?: string; message?: string } } })?.response;
  if (!resp) return 'Không kết nối được máy chủ — kiểm tra mạng rồi thử lại.';
  const code = resp.data?.code;
  const server = redactMoney(str(resp.data?.message));
  if (code && CODE_TEXT[code]) return CODE_TEXT[code](server);
  return friendlyApiMessage(server, `Lỗi ${resp.status ?? ''}`.trim());
};
